import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Users, Search, AlertTriangle, Mail, Calendar,
  GraduationCap, School, ShieldQuestion, RefreshCw, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { adminUsersAPI } from '../utils/api';

const ROLE_META = {
  teacher: { label: 'Professor',   color: 'bg-indigo-100 text-indigo-700 border-indigo-200', Icon: GraduationCap },
  student: { label: 'Aluno',        color: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: School },
  '':       { label: 'Sem papel',   color: 'bg-amber-100 text-amber-800 border-amber-300', Icon: ShieldQuestion },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META[''];
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, tint, Icon, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl p-4 border-2 shadow-sm ${tint}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-nunito font-bold uppercase tracking-wide opacity-80">{label}</span>
      <Icon className="w-4 h-4 opacity-80" />
    </div>
    <div className="text-3xl font-outfit font-black mt-1 leading-none">{value}</div>
  </div>
);

export default function AdminUsers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ users: [], total: 0, without_classroom: 0, by_role: {} });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all | teacher | student | none
  const [onlyOrphans, setOnlyOrphans] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await adminUsersAPI.listAll();
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.users || []).filter((u) => {
      if (onlyOrphans && u.has_classroom) return false;
      if (roleFilter !== 'all') {
        if (roleFilter === 'none') { if (u.role) return false; }
        else if (u.role !== roleFilter) return false;
      }
      if (!q) return true;
      const hay = `${u.name} ${u.email} ${(u.classrooms || []).map(c => c.name).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data.users, search, roleFilter, onlyOrphans]);

  const exportCsv = () => {
    const rows = [['Nome', 'Email', 'Papel', 'Cadastro', 'Salas']];
    filtered.forEach((u) => {
      rows.push([
        u.name,
        u.email,
        (ROLE_META[u.role] || ROLE_META['']).label,
        formatDate(u.created_at),
        (u.classrooms || []).map(c => c.name).join(' | ') || 'SEM SALA',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios-plataforma-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV com ${filtered.length} usuário(s) exportado`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/85 backdrop-blur-sm border-b-2 border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/teacher/dashboard')}
              data-testid="users-back-btn"
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center shadow-md">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-outfit font-black text-xl text-slate-900 leading-none">Usuários da Plataforma</h1>
                <p className="text-xs text-slate-500 font-nunito mt-1">Todos os cadastros e suas salas vinculadas</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => load(true)}
              disabled={refreshing}
              data-testid="users-refresh-btn"
              className="rounded-xl"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              onClick={exportCsv}
              data-testid="users-export-btn"
              className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Total"
            value={data.total}
            Icon={Users}
            tint="bg-white border-slate-200 text-slate-800"
            testId="stat-total"
          />
          <StatCard
            label="Professores"
            value={data.by_role?.teacher || 0}
            Icon={GraduationCap}
            tint="bg-indigo-50 border-indigo-200 text-indigo-800"
            testId="stat-teachers"
          />
          <StatCard
            label="Alunos"
            value={data.by_role?.student || 0}
            Icon={School}
            tint="bg-emerald-50 border-emerald-200 text-emerald-800"
            testId="stat-students"
          />
          <StatCard
            label="Sem sala vinculada"
            value={data.without_classroom}
            Icon={AlertTriangle}
            tint="bg-amber-50 border-amber-300 text-amber-800"
            testId="stat-orphans"
          />
        </div>

        {/* Toolbar */}
        <Card className="p-4 rounded-2xl border-2 border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="users-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, email ou sala…"
                className="pl-9 rounded-xl border-2"
              />
            </div>

            <div className="flex items-center gap-1 flex-wrap">
              {[
                { key: 'all',     label: 'Todos' },
                { key: 'teacher', label: 'Professores' },
                { key: 'student', label: 'Alunos' },
                { key: 'none',    label: 'Sem papel' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setRoleFilter(f.key)}
                  data-testid={`role-filter-${f.key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold font-nunito border-2 transition
                    ${roleFilter === f.key
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                    }`}
                >
                  {f.label}
                </button>
              ))}
              <button
                onClick={() => setOnlyOrphans((v) => !v)}
                data-testid="filter-only-orphans"
                className={`px-3 py-1.5 rounded-full text-xs font-bold font-nunito border-2 transition inline-flex items-center gap-1
                  ${onlyOrphans
                    ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                    : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                  }`}
              >
                <AlertTriangle className="w-3 h-3" />
                Somente sem sala
              </button>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-500 font-nunito">
            Exibindo <span className="font-bold text-slate-800">{filtered.length}</span> de <span className="font-bold text-slate-800">{data.total}</span>
          </div>
        </Card>

        {/* Table */}
        <Card className="rounded-2xl border-2 border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-slate-500 font-nunito">Carregando usuários…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-slate-500 font-nunito">Nenhum usuário encontrado com os filtros atuais.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 border-b-2 border-slate-200 text-slate-600">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-nunito font-bold">Usuário</th>
                    <th className="px-4 py-3 font-nunito font-bold">Email</th>
                    <th className="px-4 py-3 font-nunito font-bold">Papel</th>
                    <th className="px-4 py-3 font-nunito font-bold">Cadastro</th>
                    <th className="px-4 py-3 font-nunito font-bold">Sala(s)</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const orphan = !u.has_classroom;
                    return (
                      <tr
                        key={u.user_id}
                        data-testid={`user-row-${u.user_id}`}
                        data-orphan={orphan ? 'true' : 'false'}
                        className={`border-b border-slate-100 last:border-0 transition
                          ${orphan
                            ? 'bg-amber-50/70 hover:bg-amber-100/70'
                            : 'hover:bg-slate-50'
                          }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img
                                src={u.picture}
                                alt={u.name}
                                className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white font-outfit font-black flex items-center justify-center shadow-sm">
                                {(u.name || '?').charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-outfit font-bold text-slate-900 truncate">{u.name}</div>
                              {orphan && (
                                <div className="flex items-center gap-1 text-[11px] font-bold text-amber-700 mt-0.5">
                                  <AlertTriangle className="w-3 h-3" />
                                  Sem sala vinculada
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700 font-nunito">
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <span className="truncate max-w-[240px]" title={u.email}>{u.email}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                        <td className="px-4 py-3 text-slate-600 font-nunito text-xs">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            {formatDate(u.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {u.classrooms && u.classrooms.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[280px]">
                              {u.classrooms.map((c) => (
                                <Badge
                                  key={c.classroom_id}
                                  className="bg-indigo-100 text-indigo-800 border border-indigo-200 font-nunito font-bold hover:bg-indigo-200 cursor-default"
                                >
                                  {c.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold shadow-sm">
                              <AlertTriangle className="w-3 h-3" />
                              SEM SALA
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
